import type {
  HotelOfferView,
  HotelResultsFilterValue,
  HotelResultsOrder,
} from '@voll-intelligence/intelligence-ui-kit';

import {
  HOTEL_PANEL_AMENITIES_CAP,
  HOTEL_PANEL_BEYOND_CAP,
  HOTEL_PANEL_BEYOND_TITLE_CAP,
  HOTEL_PANEL_VISIBLE_CAP,
  type ChatUiHotelOffer,
  type HotelPanelFilters,
  type HotelPanelState,
} from '@voll-intelligence/genui-contract';
import type { PanelReport } from './use_panel_report.js';
import {
  EMPTY_HOTEL_PANEL_QUERY,
  type HotelPanelList,
  type HotelPanelQuery,
} from './use_hotel_panel_list.js';

/** `HotelResultsFilterValue` (ui-kit) → filtros do wire, só o que está LIGADO.
 *  `undefined` quando nada está ligado — a chave `filters` some do `panel`. */
export function panelFiltersForWire(
  filters: HotelResultsFilterValue,
): HotelPanelFilters | undefined {
  const wire: HotelPanelFilters = {
    ...(filters.policyOnly ? { policyOnly: true } : {}),
    ...(filters.breakfastOnly ? { breakfastOnly: true } : {}),
    ...(filters.refundableOnly ? { refundableOnly: true } : {}),
    ...(filters.minimumNightlyPrice !== undefined
      ? { minNightlyPrice: filters.minimumNightlyPrice }
      : {}),
    ...(filters.maximumNightlyPrice !== undefined
      ? { maxNightlyPrice: filters.maximumNightlyPrice }
      : {}),
    ...(filters.ratingBands?.length ? { ratingFloors: filters.ratingBands.map(Number) } : {}),
    ...(filters.maximumDistance !== undefined ? { maxDistanceKm: filters.maximumDistance } : {}),
    ...(filters.amenityIds.length > 0
      ? { amenityIds: filters.amenityIds.slice(0, HOTEL_PANEL_AMENITIES_CAP) }
      : {}),
  };
  return Object.keys(wire).length > 0 ? wire : undefined;
}

/** Os NOMES das linhas visíveis fora de `offers` (#3325): na ordem da tela, sem
 *  nome em branco, cada um cortado em `HOTEL_PANEL_BEYOND_TITLE_CAP`, a lista
 *  em `HOTEL_PANEL_BEYOND_CAP`. `undefined` quando nenhuma linha está fora —
 *  a chave some do `panel` (é payload do WAF). */
function visibleBeyondOf(
  views: readonly Pick<HotelOfferView, 'id' | 'name'>[],
  indexById: ReadonlyMap<string, number>,
): string[] | undefined {
  const names = views
    .filter((view) => !indexById.has(view.id))
    .map((view) => view.name.trim().slice(0, HOTEL_PANEL_BEYOND_TITLE_CAP))
    .filter((name) => name.length > 0)
    .slice(0, HOTEL_PANEL_BEYOND_CAP);
  return names.length > 0 ? names : undefined;
}

/** O estado do painel como o AGENTE precisa lê-lo. `undefined` quando não há
 *  nada a reportar (fechado, sem filtro, ordem padrão, gaveta fechada) — o
 *  contexto sai como na Onda 1. `visible` são ÍNDICES em `offers` (as ofertas
 *  que o agente conhece); linhas paginadas fora de `offers` contam em `shown`
 *  e saem NOMEADAS em `visibleBeyond` (#3325: o filtro do painel deixou 4 de
 *  14, dois deles paginados, e o agente só sabia nomear os da cena). `shown`/
 *  `visible`/`visibleBeyond` saem com o painel ABERTO e também FECHADO com
 *  filtro ou ordem do viajante (#2857): quem filtra, fecha e pergunta "quais
 *  sobraram?" precisa que o agente saiba o que o filtro deixou — a lista
 *  continua filtrada atrás do painel fechado. */
export function hotelPanelState(input: {
  open: boolean;
  order: HotelResultsOrder;
  filters: HotelResultsFilterValue;
  views: readonly Pick<HotelOfferView, 'id' | 'name'>[];
  offers: readonly Pick<ChatUiHotelOffer, 'offerId'>[];
  openOfferId: string | null;
}): HotelPanelState | undefined {
  const filters = panelFiltersForWire(input.filters);
  const sortBy = input.order === 'recommended' ? undefined : input.order;
  if (!input.open && !filters && !sortBy && input.openOfferId === null) return undefined;
  const indexById = new Map(input.offers.map((offer, index) => [offer.offerId, index] as const));
  const visible = input.views
    .map((view) => indexById.get(view.id))
    .filter((index): index is number => index !== undefined)
    .slice(0, HOTEL_PANEL_VISIBLE_CAP);
  const visibleBeyond = visibleBeyondOf(input.views, indexById);
  const listReported = input.open || filters !== undefined || sortBy !== undefined;
  return {
    open: input.open,
    ...(sortBy ? { sortBy } : {}),
    ...(filters ? { filters } : {}),
    ...(listReported ? { shown: input.views.length, visible } : {}),
    ...(listReported && visibleBeyond ? { visibleBeyond } : {}),
    ...(input.openOfferId ? { openOfferId: input.openOfferId } : {}),
  };
}

/** A assinatura de uma query no vocabulário do FIO (`sortBy`/`filters`, o
 *  mesmo formato que `hotelPanelState` relata) — comparar NESTE nível, e não
 *  no `HotelResultsFilterValue` cru do ui-kit, é o que faz duas queries
 *  equivalentes (mesmo eixo ligado, identidade de objeto/ordem de chave
 *  diferentes) contarem como iguais. */
function panelQuerySignature(query: HotelPanelQuery): string {
  const sortBy = query.order === 'recommended' ? undefined : query.order;
  return JSON.stringify({ sortBy, filters: panelFiltersForWire(query.filters) });
}

/** O relato do organismo (`onPanelReport`): junta `panel` (undefined quando não
 *  há nada a reportar) e `total` (undefined quando o fornecedor ainda não
 *  respondeu a contagem). `shown`/`visible` saem de `visibleViews` — o que a
 *  tela MOSTRA depois do corte de cliente —, nunca de `views` (a página crua,
 *  que inclui o que o filtro escondeu; #2857). Fora do organismo pelo teto de
 *  linhas dele — ele já vive perto de `max-lines-per-function`.
 *
 *  M1 · Onda 2 (achado #4) — `seed` é a query com que o painel NASCEU
 *  (`directivesToPanelQuery(ui.directives)`, a diretiva do próprio agente). Se
 *  a query corrente ainda é a mesma, o relato finge query VAZIA (ordem
 *  padrão, sem filtro): senão o agente leria de volta a própria diretiva como
 *  se o viajante a tivesse pedido e poderia reemiti-la em loop. Um painel
 *  fechado que não saiu da semente cai no "nada a reportar" de
 *  `hotelPanelState`; um painel ABERTO na semente continua reportando
 *  `open`/`shown`/`visible` — só `sortBy`/`filters` somem. Quando a query
 *  DIVERGE da semente (o viajante mexeu), o relato é o de sempre. */
export function panelScreenOf(
  list: Pick<HotelPanelList, 'query' | 'visibleViews' | 'total'>,
  surfacesOpen: boolean,
  effectiveOffers: readonly Pick<ChatUiHotelOffer, 'offerId'>[],
  seed?: HotelPanelQuery,
): PanelReport {
  const reportedQuery =
    seed && panelQuerySignature(list.query) === panelQuerySignature(seed)
      ? EMPTY_HOTEL_PANEL_QUERY
      : list.query;
  const panel = hotelPanelState({
    open: surfacesOpen,
    order: reportedQuery.order,
    filters: reportedQuery.filters,
    // O que a TELA mostra (corte de cliente + ordem), não a página crua (#2857).
    views: list.visibleViews,
    offers: effectiveOffers,
    openOfferId: null, // a gaveta é do CARD (`detailsTarget`), que a acrescenta
  });
  return {
    ...(panel ? { panel } : {}),
    ...(list.total === undefined ? {} : { total: list.total }),
  };
}
